import React from 'react';
import { Calendar, MapPin, ArrowUpRight } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import LeadForm from '../components/LeadForm';
import { useContent } from '../context/ContentContext';

const NAVY = '#0B2545';
const ORANGE = '#F47A1F';
const GREEN = '#4FA829';
const STAT_COLORS = [ORANGE, GREEN, NAVY];

const D = {
  eyebrow: 'One Energy Club presents', title: 'India Energy Cricket League', tagline: 'Compete. Connect. Celebrate.',
  description: "The nation's favourite sport unites with the nation's biggest sector — a first-of-its-kind corporate cricket league for the entire energy & infrastructure ecosystem.",
  date: 'May 2027', location: 'New Delhi',
  logo: 'https://customer-assets-lqy194kg.emergentagent.net/job_enersider-revamp/artifacts/7bypwogm_ChatGPT%20Image%20Oct%201%2C%202026%2C%2005_10_34%20PM.png',
  stats: [], franchises: [], franchisesEyebrow: 'Different sectors. One field. One goal.', franchisesTitle: 'Meet the franchises',
  franchisesNote: '', formEyebrow: 'Launch edition 2027', formTitle: 'Own a franchise. Join the league.',
  formIntro: "Tell us how you'd like to be part of IECL and we'll share the full prospectus.",
  interestOptions: ['Buy a sector-focused franchise', 'Sponsor', 'Exhibit', 'Become a broadcast partner'],
};

const pickFrom = (src) => (k) => {
  const x = src[k];
  return x !== undefined && x !== null && x !== '' && !(Array.isArray(x) && !x.length) ? x : D[k];
};

export default function IECL() {
  const { iecl } = useContent();
  const v = pickFrom(iecl || {});
  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="iecl-page">
      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto">
          <div className="relative rounded-3xl overflow-hidden" style={{ background: `radial-gradient(circle at 70% 20%, #12335f, ${NAVY} 70%)` }}>
            <div className="relative p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
              <Reveal>
                <div className="text-white">
                  <p className="eyebrow mb-3" style={{ color: ORANGE }} data-testid="iecl-eyebrow">{v('eyebrow')}</p>
                  <h1 className="font-display text-5xl md:text-7xl leading-[0.96]" data-testid="iecl-title">{v('title')}</h1>
                  <p className="text-xl mt-4 font-grotesk tracking-wide" style={{ color: GREEN }} data-testid="iecl-tagline">{v('tagline')}</p>
                  <p className="text-white/75 text-lg mt-4 max-w-xl" data-testid="iecl-description">{v('description')}</p>
                  <div className="flex flex-wrap gap-5 mt-6 text-sm text-white/85">
                    <span className="inline-flex items-center gap-1.5" data-testid="iecl-date"><Calendar size={16} style={{ color: ORANGE }} /> {v('date')}</span>
                    <span className="inline-flex items-center gap-1.5" data-testid="iecl-location"><MapPin size={16} style={{ color: GREEN }} /> {v('location')}</span>
                  </div>
                  <a href="#interest" className="inline-flex items-center gap-2 mt-8 font-semibold px-6 py-3.5 rounded-full text-white" style={{ background: ORANGE }} data-testid="iecl-register-cta">Register your interest <ArrowUpRight size={18} /></a>
                </div>
              </Reveal>
              <Reveal delay={120}>
                <div className="flex items-center justify-center">
                  <img src={v('logo')} alt="IECL — India Energy Cricket League" className="w-full max-w-md drop-shadow-2xl" data-testid="iecl-logo" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad py-16">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
            {v('stats').map((s, i) => (
              <div key={i} className="border-l-2 pl-5" style={{ borderColor: STAT_COLORS[i % 3] }} data-testid={`iecl-stat-${i}`}>
                <p className="font-display text-4xl md:text-5xl" style={{ color: STAT_COLORS[i % 3] }}>{s.value}</p>
                <p className="text-sm text-[#666666] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="eyebrow mb-3" style={{ color: GREEN }}>{v('franchisesEyebrow')}</p>
          <h2 className="font-display text-3xl md:text-4xl mb-8">{v('franchisesTitle')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {v('franchises').map((fr, i) => (
              <Reveal key={i} delay={(i % 5) * 50}>
                <div className="bg-[#f4f4f4] rounded-2xl p-5 h-full border-t-4" style={{ borderColor: i % 2 ? GREEN : ORANGE }} data-testid={`iecl-franchise-${i}`}>
                  <p className="font-display text-lg leading-snug">{fr.value}</p>
                  <p className="text-xs uppercase tracking-wide text-[#666666] mt-1">{fr.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {v('franchisesNote') ? <p className="text-xs text-[#666666] mt-4">{v('franchisesNote')}</p> : null}
        </div>
      </section>

      <section id="interest" className="section-pad py-10">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div>
              <p className="eyebrow mb-3" style={{ color: ORANGE }}>{v('formEyebrow')}</p>
              <h2 className="font-display text-4xl md:text-5xl">{v('formTitle')}</h2>
              <p className="text-[#666666] mt-4 max-w-md">{v('formIntro')}</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <LeadForm type="IECL Interest" recipient="partnerships@enersider.com" accent={ORANGE}
              extraFields={[
                { k: 'interest', l: 'Interested to', options: v('interestOptions'), req: true, full: true },
                { k: 'sector', l: 'Preferred sector (if buying a franchise)', full: true },
                { k: 'message', l: 'Message', area: true, full: true },
              ]}
              cta="Register interest" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
